import { AlertDTO, MonitorDTO } from '@meshwatch/types';
import { Dictionary } from 'lodash';

import { DynamoDBClient, DEFAULT_DYNAMO_DB_CLIENT } from '../../shared/aws/dynamodb';
import { ALERT_ANY_MONITOR_SCHEDULER } from '../alerts/service';
import { AlertDynamoItem } from '../alerts/types';
import DynamoMonitoringDatasource from '../db/datasource';
import { MONITORING_TABLE_SCHEDULER_GSI } from '../db/schema';
import { MonitorDynamoItem } from '../monitors/types';

type ExecutorWorkload = {
  monitors: MonitorDTO[];
  monitorAlerts: Dictionary<AlertDTO[]>;
};

export interface ExecutorsWorkloadDatasource {
  getWorkload: (scheduler: string) => Promise<ExecutorWorkload>;
}

export class DynamoExecutorsWorkloadDatasource extends DynamoMonitoringDatasource
  implements ExecutorsWorkloadDatasource {
  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super(dynamoClient);
  }

  private isMonitor = (Item: MonitorDynamoItem | AlertDynamoItem): Item is MonitorDynamoItem => {
    return Item.sortKey.startsWith('monitor');
  };

  public getWorkload = (scheduler: string): Promise<ExecutorWorkload> => {
    const workloadPromise = this.dynamoClient.queryTable({
      KeyConditionExpression: 'scheduler = :scheduler',
      IndexName: MONITORING_TABLE_SCHEDULER_GSI,
      ExpressionAttributeValues: {
        ':scheduler': scheduler,
      },
    });

    const alertsAnySchedulerPromise = this.dynamoClient.queryTable({
      KeyConditionExpression: 'scheduler = :scheduler',
      IndexName: MONITORING_TABLE_SCHEDULER_GSI,
      ExpressionAttributeValues: {
        ':scheduler': ALERT_ANY_MONITOR_SCHEDULER,
      },
    });

    return Promise.all([workloadPromise, alertsAnySchedulerPromise]).then(
      ([workloadResp, alertsAnySchedulerResp]) => {
        const alertsAnyScheduler = alertsAnySchedulerResp.Items as AlertDynamoItem[];
        const monitorAlerts: Dictionary<AlertDTO[]> = {
          [ALERT_ANY_MONITOR_SCHEDULER]: alertsAnyScheduler.map(this.alertFromDynamoItem),
        };

        const workloadItems = workloadResp.Items as (MonitorDynamoItem | AlertDynamoItem)[];
        const monitors: MonitorDTO[] = [];

        for (let Item of workloadItems) {
          if (this.isMonitor(Item)) {
            monitors.push(this.monitorFromDynamoItem(Item));
          } else {
            const alert = this.alertFromDynamoItem(Item);
            const alerts = monitorAlerts[alert.monitorId] || [];
            alerts.push(alert);
            monitorAlerts[alert.monitorId] = alerts;
          }
        }

        return { monitors, monitorAlerts };
      }
    );
  };
}

const dynamoExecutorsWorkloadDatasource = new DynamoExecutorsWorkloadDatasource();

export default dynamoExecutorsWorkloadDatasource;
